import React, { FC } from "react";

import { useBoolean } from "~/hooks";

import {
  ProgressTableBase,
  ProgressTableColumn,
  ProgressTableEmployee,
  ProgressTableCellProps,
} from "./ProgressTableBase";

import {
  EmployeeConditionSurveysQuestionWithAnswers,
  TextAreaType,
} from "~/domains";

import { Button } from "~/ui";

type RenderCellProps = ProgressTableCellProps & {
  question: EmployeeConditionSurveysQuestionWithAnswers;
};

type TextAreaQuestionProps = {
  question: EmployeeConditionSurveysQuestionWithAnswers;
  questionIndex: number;
};

type ProgressTableWithQuestionsProps = {
  columns: ProgressTableColumn[];
  questions: EmployeeConditionSurveysQuestionWithAnswers[];
  renderCell: (props: RenderCellProps) => React.ReactNode;
  className?: string;
};

// TextArea質問表示コンポーネント
const TextAreaQuestionDisplay: FC<TextAreaQuestionProps> = ({
  question,
  questionIndex,
}) => {
  const { isChecked, toggle } = useBoolean(false);

  // 回答を持つ日次回答のみフィルタリングし、日付順にソート
  // 本来日付順のソートはBackend側で行いたいが、employeeAnswersの日付順が大変なため、こちらでソートを行っている
  const answersWithText = question.employeeAnswers
    .flatMap((employeeAnswer) =>
      employeeAnswer.dailyAnswers
        .filter((dailyAnswer) => dailyAnswer.answerText)
        .map((dailyAnswer, index) => ({
          key: `${employeeAnswer.employee.id}-${index}`,
          employeeName: employeeAnswer.employee.name,
          date: dailyAnswer.date,
          answerText: dailyAnswer.answerText,
        })),
    )
    .sort((a, b) => a.date.localeCompare(b.date))
    .reverse();

  return (
    <div className="bg-secondary-50 rounded-xl border border-secondary-200">
      <div className="bg-secondary-100 px-6 py-4 border-b border-secondary-200 rounded-t-lg mb-4">
        <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
          <span className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full mr-3">
            Q{questionIndex + 1}
          </span>
          {question.question}
        </h3>
      </div>
      <div className="p-4">
        <div className="text-center">
          <Button
            onClick={toggle}
            color="primary"
            size="md"
            outline={false}
            text={isChecked ? "回答一覧を隠す" : "回答一覧を見る"}
          />
        </div>
        {isChecked && (
          <div className="mt-4 space-y-2">
            <div className="border-t border-secondary-200 pt-3">
              <h4 className="text-secondary-900 font-medium mb-3 text-base">
                回答詳細
              </h4>
              <div className="space-y-2">
                {answersWithText.map((answer) => (
                  <div
                    key={answer.key}
                    className="bg-white rounded-lg border border-secondary-200 p-3 text-sm"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-secondary-900 text-sm">
                        {answer.employeeName}
                      </div>
                      <div className="text-xs text-secondary-600">
                        {answer.date}
                      </div>
                    </div>
                    <div className="text-secondary-800 leading-relaxed whitespace-pre-wrap">
                      {answer.answerText}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ProgressTableWithQuestions: FC<
  ProgressTableWithQuestionsProps
> = ({ columns, questions, renderCell, className = "" }) => {
  const createTableHeader = (questionIndex: number, questionText: string) => (
    <div className="bg-secondary-100 px-6 py-4 border-b border-secondary-200">
      <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
        <span className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full mr-3">
          Q{questionIndex + 1}
        </span>
        {questionText}
      </h3>
    </div>
  );

  // 通常の質問をレンダリング
  const renderRegularQuestion = (
    question: EmployeeConditionSurveysQuestionWithAnswers,
    questionIndex: number,
  ) => {
    const employees: ProgressTableEmployee[] = question.employeeAnswers.map(
      (employeeAnswer) => employeeAnswer.employee,
    );

    const questionRenderCell = (props: ProgressTableCellProps) =>
      renderCell({ ...props, question });

    return (
      <ProgressTableBase
        key={question.id}
        columns={columns}
        employees={employees}
        renderCell={questionRenderCell}
        tableHeader={createTableHeader(questionIndex, question.question)}
      />
    );
  };

  return (
    <div className={`space-y-8 ${className}`}>
      {questions.map((question, index) => {
        if (question.questionTypeId === TextAreaType.id) {
          return (
            <TextAreaQuestionDisplay
              key={question.id}
              question={question}
              questionIndex={index}
            />
          );
        }

        return renderRegularQuestion(question, index);
      })}
    </div>
  );
};
